import React, { useEffect, useState } from "react";
import { Check, CigaretteOff, ClipboardList, Flame, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import ForecastPlaceholder from "../components/common/ForecastPlaceholder";
import ForecastStrip from "../components/common/ForecastStrip";
import { fetchSevenDayForecast } from "../services/forecastApi";
import { fetchProgressDashboard } from "../services/progressTrackingApi";
import { formatDisplayDate } from "../utils/dateFormat";
import "./DashboardPage.css";

function getActivityIcon(activityType = "") {
  if (activityType.startsWith("quit_plan")) return <ClipboardList />;
  if (activityType.startsWith("checkin")) return <CigaretteOff />;
  return <Check />;
}

function getActivityTitle(item) {
  return item.title || "Activity saved";
}

function getActivitySubtitle(item) {
  const dateLabel = formatDisplayDate(item.activity_at || item.progress_date);
  const subtitle =
    item.subtitle ||
    (item.status === "SMOKE_FREE" ? "Great progress!" : item.status || "");

  return [dateLabel, subtitle].filter(Boolean).join(" · ");
}

const DashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    const loadDashboard = async () => {
      try {
        const [data, forecast] = await Promise.all([
          fetchProgressDashboard(year, month),
          fetchSevenDayForecast(),
        ]);
        if (!cancelled) {
          setDashboard(data);
          setForecastData(forecast);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load dashboard.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <main className="dashboard-page">
        <div className="dashboard-page__container">
          <p className="dashboard-page__status">Loading your dashboard...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="dashboard-page">
        <div className="dashboard-page__container">
          <p className="dashboard-page__error" role="alert">
            {error}
          </p>
        </div>
      </main>
    );
  }

  const latestProgress = dashboard?.recent_history?.[0] || null;
  const recentActivities = dashboard?.recent_activities || dashboard?.recent_history || [];

  return (
    <main className="dashboard-page">
      <div className="dashboard-page__container">
        <header className="dashboard-page__header">
          <h1 className="dashboard-page__title">Dashboard</h1>
          <p className="dashboard-page__subtitle">Quit Journey</p>
        </header>

        <section className="dashboard-hero">
          <div className="dashboard-hero__content">
            <h2 className="dashboard-hero__heading">Your Quit Progress</h2>

            <div className="dashboard-hero__stats">
              <div className="dashboard-hero__stat">
                <span className="dashboard-hero__number">
                  {dashboard.smoke_free_days || 0}
                </span>
                <span className="dashboard-hero__label">Days Smoke-Free</span>
              </div>

              <div className="dashboard-hero__stat">
                <span className="dashboard-hero__number">
                  {dashboard.smoke_free_days || 0}
                </span>
                <span className="dashboard-hero__label">Days Recorded</span>
              </div>

              <div className="dashboard-hero__stat">
                <span className="dashboard-hero__number">
                  ${dashboard.money_saved || 0}
                </span>
                <span className="dashboard-hero__label">Money Saved</span>
              </div>
            </div>

            <div className="dashboard-hero__improvements">
              <p className="dashboard-hero__improvements-title">
                Health Improvements
              </p>
              <div className="dashboard-hero__chips">
                <span className="dashboard-chip">Smoke-free progress</span>
                <span className="dashboard-chip">Streak building</span>
              </div>
            </div>
          </div>
        </section>

        <section className="dashboard-summary-cards">
          <article className="dashboard-summary-card">
            <div className="dashboard-summary-card__icon"><Flame /></div>
            <h2 className="dashboard-summary-card__value">
              {dashboard.current_streak || 0}
            </h2>
            <p className="dashboard-summary-card__label">Day Streak</p>
          </article>

          <article className="dashboard-summary-card">
            <div className="dashboard-summary-card__icon"><CigaretteOff /></div>
            <h2 className="dashboard-summary-card__value">
              {dashboard.smoke_free_days || 0}
            </h2>
            <p className="dashboard-summary-card__label">Days Avoided</p>
          </article>

          <article className="dashboard-summary-card">
            <div className="dashboard-summary-card__icon"><Trophy /></div>
            <h2 className="dashboard-summary-card__value">
              {dashboard.best_streak || 0}
            </h2>
            <p className="dashboard-summary-card__label">Best Streak</p>
          </article>
        </section>

        {forecastData?.forecast_available ? (
          <ForecastStrip forecast={forecastData.forecast} />
        ) : (
          <ForecastPlaceholder checkinCount={forecastData?.checkin_count || 0} />
        )}

        <section className="dashboard-feature-grid">
          <article className="dashboard-feature-card dashboard-feature-card--green">
            <div className="dashboard-feature-card__icon" aria-hidden="true">
              <Check />
            </div>
            <h3 className="dashboard-feature-card__title">Progress</h3>
            <p className="dashboard-feature-card__text">
              Detailed graphs and calendar
            </p>
            <Link
              to="/progress-tracking"
              className="dashboard-feature-card__link"
            >
              View progress
            </Link>
          </article>

          <article className="dashboard-feature-card dashboard-feature-card--blue">
            <div className="dashboard-feature-card__icon" aria-hidden="true">
              <ClipboardList />
            </div>
            <h3 className="dashboard-feature-card__title">Quit Plan</h3>
            <p className="dashboard-feature-card__text">
              Personal coping steps for cravings and high risk moments
            </p>
            <Link to="/quit-plan" className="dashboard-feature-card__link">
              Open quit plan
            </Link>
          </article>

          <article className="dashboard-feature-card dashboard-feature-card--purple">
            <div className="dashboard-feature-card__icon" aria-hidden="true">
              <CigaretteOff />
            </div>
            <h3 className="dashboard-feature-card__title">Check-In</h3>
            <p className="dashboard-feature-card__text">
              Complete your smoking risk check-in
            </p>
            <Link to="/check-in" className="dashboard-feature-card__link">
              Start check in
            </Link>
          </article>
        </section>

        <section className="dashboard-panel">
          <div className="dashboard-panel__header">
            <h2 className="dashboard-panel__title">Recent Activity</h2>
          </div>

          {recentActivities.length ? (
            <ul className="dashboard-activity-list">
              {recentActivities.slice(0, 4).map((item) => (
                <li
                  key={item.activity_id || item.progress_id}
                  className="dashboard-activity-item"
                >
                  <div className="dashboard-activity-item__icon">
                    {getActivityIcon(item.activity_type)}
                  </div>

                  <div className="dashboard-activity-item__content">
                    <p className="dashboard-activity-item__title">
                      {getActivityTitle(item)}
                    </p>
                    <p className="dashboard-activity-item__subtitle">
                      {getActivitySubtitle(item)}
                    </p>
                  </div>

                  <span className="dashboard-activity-item__time">
                    {item.badge || "SAVED"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="dashboard-panel__empty">
              No recent activity yet. Start with progress, a check-in, or a quit plan.
            </p>
          )}
        </section>

        <section className="dashboard-panel">
          <div className="dashboard-panel__header">
            <h2 className="dashboard-panel__title">Latest Progress</h2>
          </div>

          {latestProgress ? (
            <div className="dashboard-latest">
              <div className="dashboard-latest__left">
                <p className="dashboard-latest__date">
                  {formatDisplayDate(latestProgress.progress_date)}
                </p>
                <p className="dashboard-latest__line">
                  Status:{" "}
                  {latestProgress.status === "SMOKE_FREE"
                    ? "Smoke-Free"
                    : latestProgress.status}
                </p>
                <p className="dashboard-latest__line">
                  Current streak: {dashboard.current_streak || 0} day
                  {dashboard.current_streak === 1 ? "" : "s"}
                </p>
              </div>

              <span className="dashboard-latest__badge">SMOKE-FREE</span>
            </div>
          ) : (
            <p className="dashboard-panel__empty">No progress recorded yet.</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default DashboardPage;
